/**
 * QC Profiles Editor — Super Admin only
 *
 * Manage broadcast QC profiles stored in Firestore.
 * Each profile defines loudness, peak, format, sample rate, and clipping thresholds.
 * Profiles are used by the QC service and selected in QCHistoryModal.
 */

import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Input,
  Label,
  Spinner,
  Table,
} from 'reactstrap';
import { FirebaseContext } from '../../components/Firebase';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';

interface QCProfile {
  id: string;
  name: string;
  loudness_target: number;
  loudness_tolerance: number;
  max_true_peak: number;
  required_format: string;
  min_bit_depth: number;
  allowed_sample_rates: number[];
  channel_config: string;
  max_clipping_events: number;
}

const DEFAULT_PROFILES: Record<string, Omit<QCProfile, 'id'>> = {
  ebu_r128: {
    name: 'EBU R128 (Europe)',
    loudness_target: -23.0,
    loudness_tolerance: 1.0,
    max_true_peak: -2.0,
    required_format: 'wav',
    min_bit_depth: 16,
    allowed_sample_rates: [44100, 48000],
    channel_config: 'stereo',
    max_clipping_events: 0,
  },
  atsc_a85: {
    name: 'ATSC A/85 (North America)',
    loudness_target: -24.0,
    loudness_tolerance: 2.0,
    max_true_peak: -2.0,
    required_format: 'wav',
    min_bit_depth: 16,
    allowed_sample_rates: [44100, 48000],
    channel_config: 'stereo',
    max_clipping_events: 0,
  },
  sabc: {
    name: 'SABC (South Africa)',
    loudness_target: -23.0,
    loudness_tolerance: 1.0,
    max_true_peak: -3.0,
    required_format: 'wav',
    min_bit_depth: 16,
    allowed_sample_rates: [44100, 48000],
    channel_config: 'stereo',
    max_clipping_events: 0,
  },
  relaxed: {
    name: 'Relaxed (Demo/Review)',
    loudness_target: -23.0,
    loudness_tolerance: 3.0,
    max_true_peak: -1.0,
    required_format: 'wav',
    min_bit_depth: 16,
    allowed_sample_rates: [44100, 48000],
    channel_config: 'any',
    max_clipping_events: 2,
  },
};

const QCProfiles: React.FC = () => {
  const firebase = useContext(FirebaseContext);
  const [profiles, setProfiles] = useState<QCProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<QCProfile | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);

  // Load profiles from Firestore (or seed defaults)
  useEffect(() => {
    const load = async () => {
      if (!firebase) return;
      try {
        const profilesRef = collection(firebase.db, 'qcProfiles');
        const snapshot = await getDocs(profilesRef);

        if (snapshot.empty) {
          // Seed defaults
          const seeded: QCProfile[] = [];
          for (const [id, profile] of Object.entries(DEFAULT_PROFILES)) {
            await setDoc(doc(firebase.db, 'qcProfiles', id), profile);
            seeded.push({ id, ...profile });
          }
          setProfiles(seeded);
        } else {
          setProfiles(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as QCProfile)));
        }
      } catch (err) {
        console.error('Error loading QC profiles:', err);
      }
      setLoading(false);
    };
    load();
  }, [firebase]);

  const handleEdit = (profile: QCProfile) => {
    setEditingId(profile.id);
    setEditForm({ ...profile });
    setShowNewForm(false);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
    setShowNewForm(false);
  };

  const handleNewProfile = () => {
    setShowNewForm(true);
    setEditingId(null);
    setEditForm({
      id: '',
      name: '',
      loudness_target: -23.0,
      loudness_tolerance: 1.0,
      max_true_peak: -2.0,
      required_format: 'wav',
      min_bit_depth: 16,
      allowed_sample_rates: [44100, 48000],
      channel_config: 'stereo',
      max_clipping_events: 0,
    });
  };

  const handleSave = async () => {
    if (!firebase || !editForm) return;

    const profileId = showNewForm
      ? editForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/_+$/, '')
      : editingId;

    if (!profileId) return;

    setSaving(profileId);
    try {
      const { id, ...data } = editForm;
      await setDoc(doc(firebase.db, 'qcProfiles', profileId), data);

      setProfiles(prev => {
        const existing = prev.findIndex(p => p.id === profileId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = { id: profileId, ...data };
          return updated;
        }
        return [...prev, { id: profileId, ...data }];
      });

      setEditingId(null);
      setEditForm(null);
      setShowNewForm(false);
    } catch (err) {
      console.error('Error saving QC profile:', err);
    }
    setSaving(null);
  };

  const handleDelete = async (profileId: string) => {
    if (!firebase) return;
    if (!window.confirm(`Delete profile "${profileId}"? This cannot be undone.`)) return;

    setSaving(profileId);
    try {
      await deleteDoc(doc(firebase.db, 'qcProfiles', profileId));
      setProfiles(prev => prev.filter(p => p.id !== profileId));
    } catch (err) {
      console.error('Error deleting QC profile:', err);
    }
    setSaving(null);
  };

  const handleResetDefaults = async () => {
    if (!firebase) return;
    if (!window.confirm('Reset all profiles to factory defaults? This will overwrite any changes.')) return;

    setLoading(true);
    try {
      // Delete existing
      const profilesRef = collection(firebase.db, 'qcProfiles');
      const snapshot = await getDocs(profilesRef);
      for (const d of snapshot.docs) {
        await deleteDoc(doc(firebase.db, 'qcProfiles', d.id));
      }
      // Re-seed
      const seeded: QCProfile[] = [];
      for (const [id, profile] of Object.entries(DEFAULT_PROFILES)) {
        await setDoc(doc(firebase.db, 'qcProfiles', id), profile);
        seeded.push({ id, ...profile });
      }
      setProfiles(seeded);
    } catch (err) {
      console.error('Error resetting profiles:', err);
    }
    setLoading(false);
  };

  const updateField = (field: string, value: any) => {
    setEditForm(prev => prev ? { ...prev, [field]: value } : null);
  };

  const inputStyle = {
    backgroundColor: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    color: '#fff',
    fontSize: '0.85rem',
  };

  const labelStyle = {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '0.8rem',
    marginBottom: '2px',
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner style={{ color: 'var(--accent-1-color)' }} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h4 style={{ color: '#fff', margin: 0 }}>QC Profiles</h4>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button size="sm" className="btn-primary-custom" onClick={handleNewProfile}>
            <span style={{ position: 'relative', zIndex: 2 }}>
              <i className="tim-icons icon-simple-add mr-1"></i> New Profile
            </span>
          </Button>
          <Button size="sm" className="btn-info-custom" onClick={handleResetDefaults}>
            <span style={{ position: 'relative', zIndex: 2 }}>
              <i className="tim-icons icon-refresh-02 mr-1"></i> Reset Defaults
            </span>
          </Button>
        </div>
      </div>

      {/* Profile cards */}
      <Row>
        {profiles.map(profile => {
          const isEditing = editingId === profile.id;
          const form = isEditing ? editForm : null;

          return (
            <Col md="6" key={profile.id} className="mb-3">
              <Card className="no-bg-card" style={{ border: isEditing ? '1px solid var(--accent-1-color)' : undefined }}>
                <CardHeader style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.75rem 1rem',
                }}>
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem' }}>
                    {isEditing ? 'Editing: ' : ''}{profile.name}
                  </span>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {!isEditing ? (
                      <>
                        <button onClick={() => handleEdit(profile)} style={{
                          background: 'none', border: 'none', color: 'var(--accent-1-color)', cursor: 'pointer', fontSize: '0.85rem',
                        }}>
                          <i className="tim-icons icon-pencil"></i>
                        </button>
                        <button onClick={() => handleDelete(profile.id)} disabled={saving === profile.id} style={{
                          background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: '0.85rem',
                        }}>
                          {saving === profile.id ? <Spinner size="sm" /> : <i className="tim-icons icon-trash-simple"></i>}
                        </button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" className="btn-success-custom" onClick={handleSave} disabled={saving === profile.id} style={{ fontSize: '0.75rem', padding: '2px 10px' }}>
                          <span style={{ position: 'relative', zIndex: 2 }}>{saving === profile.id ? <Spinner size="sm" /> : 'Save'}</span>
                        </Button>
                        <Button size="sm" className="btn-danger-custom" onClick={handleCancelEdit} style={{ fontSize: '0.75rem', padding: '2px 10px' }}>
                          <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
                        </Button>
                      </>
                    )}
                  </div>
                </CardHeader>
                <CardBody style={{ padding: '1rem' }}>
                  {isEditing && form ? (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <Label style={labelStyle}>Profile Name</Label>
                        <Input bsSize="sm" value={form.name} onChange={(e) => updateField('name', e.target.value)} style={inputStyle} />
                      </div>
                      <div>
                        <Label style={labelStyle}>Loudness Target (LUFS)</Label>
                        <Input bsSize="sm" type="number" step="0.1" value={form.loudness_target} onChange={(e) => updateField('loudness_target', parseFloat(e.target.value))} style={inputStyle} />
                      </div>
                      <div>
                        <Label style={labelStyle}>Loudness Tolerance (LU)</Label>
                        <Input bsSize="sm" type="number" step="0.1" value={form.loudness_tolerance} onChange={(e) => updateField('loudness_tolerance', parseFloat(e.target.value))} style={inputStyle} />
                      </div>
                      <div>
                        <Label style={labelStyle}>Max True Peak (dBTP)</Label>
                        <Input bsSize="sm" type="number" step="0.1" value={form.max_true_peak} onChange={(e) => updateField('max_true_peak', parseFloat(e.target.value))} style={inputStyle} />
                      </div>
                      <div>
                        <Label style={labelStyle}>Max Clipping Events</Label>
                        <Input bsSize="sm" type="number" step="1" value={form.max_clipping_events} onChange={(e) => updateField('max_clipping_events', parseInt(e.target.value))} style={inputStyle} />
                      </div>
                      <div>
                        <Label style={labelStyle}>Required Format</Label>
                        <Input bsSize="sm" type="select" value={form.required_format} onChange={(e) => updateField('required_format', e.target.value)} style={inputStyle}>
                          <option value="wav">WAV</option>
                          <option value="mp3">MP3</option>
                          <option value="flac">FLAC</option>
                          <option value="any">Any</option>
                        </Input>
                      </div>
                      <div>
                        <Label style={labelStyle}>Min Bit Depth</Label>
                        <Input bsSize="sm" type="select" value={form.min_bit_depth} onChange={(e) => updateField('min_bit_depth', parseInt(e.target.value))} style={inputStyle}>
                          <option value={16}>16-bit</option>
                          <option value={24}>24-bit</option>
                          <option value={32}>32-bit</option>
                        </Input>
                      </div>
                      <div>
                        <Label style={labelStyle}>Channel Config</Label>
                        <Input bsSize="sm" type="select" value={form.channel_config} onChange={(e) => updateField('channel_config', e.target.value)} style={inputStyle}>
                          <option value="stereo">Stereo</option>
                          <option value="mono">Mono</option>
                          <option value="any">Any</option>
                        </Input>
                      </div>
                      <div>
                        <Label style={labelStyle}>Allowed Sample Rates</Label>
                        <Input bsSize="sm" value={form.allowed_sample_rates.join(', ')} onChange={(e) => updateField('allowed_sample_rates', e.target.value.split(',').map((s: string) => parseInt(s.trim())).filter((n: number) => !isNaN(n)))} style={inputStyle} placeholder="44100, 48000" />
                      </div>
                    </div>
                  ) : (
                    <Table dark size="sm" style={{ backgroundColor: 'transparent', marginBottom: 0 }}>
                      <tbody>
                        <tr><td style={{ border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>Loudness</td><td style={{ border: 'none', color: '#fff', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>{profile.loudness_target} LUFS ({'\u00B1'}{profile.loudness_tolerance} LU)</td></tr>
                        <tr><td style={{ border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>True Peak</td><td style={{ border: 'none', color: '#fff', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>{profile.max_true_peak} dBTP</td></tr>
                        <tr><td style={{ border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>Format</td><td style={{ border: 'none', color: '#fff', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>{profile.required_format.toUpperCase()}, {profile.min_bit_depth}-bit, {profile.channel_config}</td></tr>
                        <tr><td style={{ border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>Sample Rates</td><td style={{ border: 'none', color: '#fff', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>{profile.allowed_sample_rates?.join(', ') || '--'}</td></tr>
                        <tr><td style={{ border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>Clipping</td><td style={{ border: 'none', color: '#fff', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}>Max {profile.max_clipping_events} events</td></tr>
                      </tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </Col>
          );
        })}

        {/* New profile form */}
        {showNewForm && editForm && (
          <Col md="6" className="mb-3">
            <Card className="no-bg-card" style={{ border: '1px solid var(--accent-1-color)' }}>
              <CardHeader style={{
                backgroundColor: 'rgba(255,255,255,0.03)',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0.75rem 1rem',
              }}>
                <span style={{ color: 'var(--accent-1-color)', fontWeight: 600, fontSize: '0.9rem' }}>New Profile</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button size="sm" className="btn-success-custom" onClick={handleSave} disabled={!editForm.name.trim() || saving !== null} style={{ fontSize: '0.75rem', padding: '2px 10px' }}>
                    <span style={{ position: 'relative', zIndex: 2 }}>{saving ? <Spinner size="sm" /> : 'Create'}</span>
                  </Button>
                  <Button size="sm" className="btn-danger-custom" onClick={handleCancelEdit} style={{ fontSize: '0.75rem', padding: '2px 10px' }}>
                    <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
                  </Button>
                </div>
              </CardHeader>
              <CardBody style={{ padding: '1rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <Label style={labelStyle}>Profile Name</Label>
                    <Input bsSize="sm" value={editForm.name} onChange={(e) => updateField('name', e.target.value)} style={inputStyle} placeholder="e.g. My Custom Profile" />
                  </div>
                  <div>
                    <Label style={labelStyle}>Loudness Target (LUFS)</Label>
                    <Input bsSize="sm" type="number" step="0.1" value={editForm.loudness_target} onChange={(e) => updateField('loudness_target', parseFloat(e.target.value))} style={inputStyle} />
                  </div>
                  <div>
                    <Label style={labelStyle}>Loudness Tolerance (LU)</Label>
                    <Input bsSize="sm" type="number" step="0.1" value={editForm.loudness_tolerance} onChange={(e) => updateField('loudness_tolerance', parseFloat(e.target.value))} style={inputStyle} />
                  </div>
                  <div>
                    <Label style={labelStyle}>Max True Peak (dBTP)</Label>
                    <Input bsSize="sm" type="number" step="0.1" value={editForm.max_true_peak} onChange={(e) => updateField('max_true_peak', parseFloat(e.target.value))} style={inputStyle} />
                  </div>
                  <div>
                    <Label style={labelStyle}>Max Clipping Events</Label>
                    <Input bsSize="sm" type="number" step="1" value={editForm.max_clipping_events} onChange={(e) => updateField('max_clipping_events', parseInt(e.target.value))} style={inputStyle} />
                  </div>
                  <div>
                    <Label style={labelStyle}>Required Format</Label>
                    <Input bsSize="sm" type="select" value={editForm.required_format} onChange={(e) => updateField('required_format', e.target.value)} style={inputStyle}>
                      <option value="wav">WAV</option>
                      <option value="mp3">MP3</option>
                      <option value="flac">FLAC</option>
                      <option value="any">Any</option>
                    </Input>
                  </div>
                  <div>
                    <Label style={labelStyle}>Min Bit Depth</Label>
                    <Input bsSize="sm" type="select" value={editForm.min_bit_depth} onChange={(e) => updateField('min_bit_depth', parseInt(e.target.value))} style={inputStyle}>
                      <option value={16}>16-bit</option>
                      <option value={24}>24-bit</option>
                      <option value={32}>32-bit</option>
                    </Input>
                  </div>
                  <div>
                    <Label style={labelStyle}>Channel Config</Label>
                    <Input bsSize="sm" type="select" value={editForm.channel_config} onChange={(e) => updateField('channel_config', e.target.value)} style={inputStyle}>
                      <option value="stereo">Stereo</option>
                      <option value="mono">Mono</option>
                      <option value="any">Any</option>
                    </Input>
                  </div>
                  <div>
                    <Label style={labelStyle}>Allowed Sample Rates</Label>
                    <Input bsSize="sm" value={editForm.allowed_sample_rates.join(', ')} onChange={(e) => updateField('allowed_sample_rates', e.target.value.split(',').map((s: string) => parseInt(s.trim())).filter((n: number) => !isNaN(n)))} style={inputStyle} placeholder="44100, 48000" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default QCProfiles;
